// src/components/CreateCommandDialog.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
} from '@mui/material';
import { createCommand } from '../services/commandService';

const defaultZones = [
  'ZONE A - LAGOS',
  'ZONE B - KADUNA',
  'ZONE B2 - KADUNA',
  'ZONE C - BAUCHI',
  'ZONE D - MINNA',
  'ZONE E - OWERRI',
  'ZONE F - IBADAN',
  'ZONE G - BENIN',
  'ZONE H - BENUE',
  'SERVICE HEADQUARTERS',
];

const defaultStates = [
  'Abia', 'Adamawa', 'Akwa Ibom', 'Anambra', 'Bauchi', 'Bayelsa', 'Benue',
  'Borno', 'Cross River', 'Delta', 'Ebonyi', 'Edo', 'Ekiti', 'Enugu',
  'FCT', 'Gombe', 'Imo', 'Jigawa', 'Kaduna', 'Kano', 'Katsina', 'Kebbi',
  'Kogi', 'Kwara', 'Lagos', 'Nasarawa', 'Niger', 'Ogun', 'Ondo', 'Osun',
  'Oyo', 'Plateau', 'Rivers', 'Sokoto', 'Taraba', 'Yobe', 'Zamfara',
];

const CreateCommandDialog = ({ open, exercises, onClose, onSuccess, onNotify }) => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    zone: '',
    state: '',
    type: '',
    exerciseId: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');

    if (!formData.code || !formData.name || !formData.zone || !formData.state || !formData.type || !formData.exerciseId) {
      setError('All fields are required');
      return;
    }

    setLoading(true);

    try {
      await createCommand(formData);

      const label = `${formData.code} - ${formData.name}`.trim();
      setFormData({
        code: '',
        name: '',
        zone: '',
        state: '',
        type: '',
        exerciseId: '',
      });
      if (onNotify) onNotify(`Command ${label} created successfully`, 'success');
      onSuccess();
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create command';
      setError(msg);
      if (onNotify) onNotify(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({
        code: '',
        name: '',
        zone: '',
        state: '',
        type: '',
        exerciseId: '',
      });
      setError('');
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Command</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <TextField
            fullWidth
            label="Command Code"
            name="code"
            value={formData.code}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
            placeholder="e.g., LASC"
            helperText="Unique code (will be converted to uppercase)"
          />

          <TextField
            fullWidth
            label="Command Name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
            placeholder="e.g., Lagos State Command"
          />

          <Autocomplete
            freeSolo
            options={defaultZones}
            value={formData.zone}
            onChange={(e, newValue) => {
              setFormData({ ...formData, zone: newValue || '' });
              setError('');
            }}
            onInputChange={(e, newValue) => {
              setFormData({ ...formData, zone: newValue || '' });
              setError('');
            }}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Zone"
                required
                margin="normal"
                placeholder="Select or type a zone name"
                helperText="Pick from suggestions or type a custom zone name"
              />
            )}
          />

          <Autocomplete
            freeSolo
            options={defaultStates}
            value={formData.state}
            onChange={(e, newValue) => {
              setFormData({ ...formData, state: newValue || '' });
              setError('');
            }}
            onInputChange={(e, newValue) => {
              setFormData({ ...formData, state: newValue || '' });
              setError('');
            }}
            disabled={loading}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="State"
                required
                margin="normal"
                placeholder="Select or type a state name"
                helperText="Pick from suggestions or type a custom state name"
              />
            )}
          />

          <TextField
            fullWidth
            select
            label="Type"
            name="type"
            value={formData.type}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          >
            <MenuItem value="STATE_COMMAND">State Command</MenuItem>
            <MenuItem value="SPECIAL_LOCATION">Special Location</MenuItem>
            <MenuItem value="HEADQUARTERS">Headquarters</MenuItem>
          </TextField>

          <TextField
            fullWidth
            select
            label="Exercise"
            name="exerciseId"
            value={formData.exerciseId}
            onChange={handleChange}
            required
            margin="normal"
            disabled={loading}
          >
            {exercises.map((exercise) => (
              <MenuItem key={exercise.id} value={exercise.id}>
                {exercise.year} - {exercise.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={24} /> : 'Create Command'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateCommandDialog;